import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib'
import { saveBlobToFile } from 'src/_utils/blobUtils';

// Cache for canvas options to avoid object creation
const CANVAS_OPTIONS = {
    useCORS: true,
    scale: 1,
    logging: false,
    allowTaint: false
};

// Cache for PDF options
const PDF_OPTIONS = {
    orientation: 'p',
    unit: 'pt',
    compress: true
};

export const mergePdfs = async (pdfsToMerges) => {
    const mergedPdf = await PDFDocument.create();
    
    // Process PDFs sequentially to reduce memory pressure
    for (const pdfBuffer of pdfsToMerges) {
        const pdf = await PDFDocument.load(pdfBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => {
            mergedPdf.addPage(page);
        });
        // Allow garbage collection of the loaded PDF
        pdf.destroy?.();
    }
    
    const mergedPdfFile = await mergedPdf.save();
    return mergedPdfFile;
}

export const generatePDF = async (barCodes, idPrefix, hideMode, fileName, onComplete) => {
    if (!hideMode || !barCodes?.length) return;
    
    const arrayBuffers = [];
    
    // Batch process elements for better performance
    for (const barcode of barCodes) {
        const selector = `#${idPrefix}${barcode.text}`;
        const divToPrint = document.querySelector(selector);
        
        if (!divToPrint) {
            console.warn(`Element not found: ${selector}`);
            continue;
        }
        
        const canvasOptions = {
            ...CANVAS_OPTIONS,
            onclone: hideMode === "display" ? function (clonedDoc) {
                clonedDoc.querySelector(selector).style.display = 'block';
            } : undefined
        };
        
        const canvas = await html2canvas(divToPrint, canvasOptions);
        const imgData = canvas.toDataURL('image/jpeg', 0.8); // Add quality parameter
        
        const report = new JsPDF({
            ...PDF_OPTIONS,
            format: [canvas.width * 0.75, canvas.height * 0.75]
        });
        
        report.addImage(imgData, 'JPEG', 0, 0);
        const arrayBuffer = report.output('arraybuffer');
        arrayBuffers.push(arrayBuffer);
        
        // Clean up canvas to free memory
        canvas.width = canvas.height = 0;
    }
    
    const response = await mergePdfs(arrayBuffers);
    const blob = new Blob([response], { type: "application/pdf" });
    saveBlobToFile(blob, `${fileName}.pdf`);
    onComplete();
}

// Optimized base64 conversion function
const arrayBufferToBase64 = (buffer) => {
    const uint8Array = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32KB chunks to avoid call stack overflow
    let binaryString = '';
    
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
        binaryString += String.fromCharCode.apply(null, chunk);
    }
    
    return btoa(binaryString);
};

export const generateBase64String = async (barCodes, idPrefix, hideMode, fileName, onComplete) => {
    try {
        if (!hideMode || !barCodes?.length) return;
        
        const arrayBuffers = [];
        
        for (const barcode of barCodes) {
            const selector = `#${idPrefix}${barcode.text}`;
            const divToPrint = document.querySelector(selector);
            
            if (!divToPrint) {
                console.warn(`Element not found: ${selector}`);
                continue;
            }
            
            const canvasOptions = {
                ...CANVAS_OPTIONS,
                onclone: hideMode === "display" ? function (clonedDoc) {
                    clonedDoc.querySelector(selector).style.display = 'block';
                } : undefined
            };
            
            const canvas = await html2canvas(divToPrint, canvasOptions);
            const imgData = canvas.toDataURL('image/jpeg', 0.8); // Add quality parameter
            
            const report = new JsPDF({
                ...PDF_OPTIONS,
                format: [canvas.width * 0.75, canvas.height * 0.75]
            });
            
            report.addImage(imgData, 'JPEG', 0, 0);
            const arrayBuffer = report.output('arraybuffer');
            arrayBuffers.push(arrayBuffer);
            
            // Clean up canvas to free memory
            canvas.width = canvas.height = 0;
        }
        
        const response = await mergePdfs(arrayBuffers);
        const b64 = arrayBufferToBase64(response);
        onComplete(b64);
    } catch (error) {
        console.error("Error generating base64 string:", error);
        onComplete(null);
    }
}